/**
 * Loading, empty and error states.
 *
 * Every list and form in this app has all three. A blank screen while data
 * loads, or after it fails, is the difference between "slow" and "broken" from
 * the reader's side — and they cannot tell which without being told.
 */

import type { ReactNode } from "react";

import { buttonClass } from "@/components/ui/controls";
import { namedProblems } from "@/lib/problems";
import type { FieldProblem } from "@/lib/types";

export function Panel({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`panel rounded-panel ${className}`}>{children}</div>;
}

export function Skeleton({ rows = 3, label }: { rows?: number; label: string }) {
  return (
    <div role="status" aria-busy="true" aria-label={label} className="grid gap-px">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="h-11 animate-pulse bg-sunk" />
      ))}
      <span className="sr-only">{label}</span>
    </div>
  );
}

/** While a page's own code is still arriving. Quiet on purpose: on a fast
 * connection it shows for a frame, and a spinner there is a flicker. */
export function PageLoading() {
  return (
    <div role="status" aria-busy="true" className="grid min-h-[50dvh] place-items-center">
      <span className="text-md text-ink-muted">Loading…</span>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div role="status" className="px-6 py-14 text-center">
      <h3 className="text-lg font-semibold text-ink">{title}</h3>
      <p className="mx-auto mt-1 max-w-sm text-md text-ink-muted">{description}</p>
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = "Something went wrong",
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div role="alert" className="px-6 py-14 text-center">
      <h3 className="text-lg font-semibold text-danger">{title}</h3>
      <p className="mx-auto mt-1 max-w-md text-md text-ink-muted">{message}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} className={`${buttonClass("secondary")} mt-4`}>
          Try again
        </button>
      )}
    </div>
  );
}

/** A short-lived confirmation. Announced politely so it is not missed. */
export function Notice({
  tone = "info",
  children,
}: {
  tone?: "info" | "danger" | "warn";
  children: ReactNode;
}) {
  const tones = {
    info: "border-rule bg-sunk text-ink",
    danger: "border-danger bg-danger-soft text-danger",
    warn: "border-warn bg-warn-soft text-warn",
  } as const;
  return (
    <div role="status" className={`rounded-control border px-3 py-2 text-md ${tones[tone]}`}>
      {children}
    </div>
  );
}

/**
 * What is wrong with a form, with each field named.
 *
 * It used to say "One field needs attention." and stop, which on a ten-input
 * form is a hunt whose answer the message already held. Naming them costs
 * nothing and is also the accessible pattern: an error summary at the top of a
 * form is what a screen reader reaches first after a failed submit.
 *
 * The per-field messages stay beside their inputs as well. That is deliberate
 * duplication — this tells you what to go and fix, the inline one tells you
 * that you have arrived.
 */
export function ProblemSummary({
  problems,
  labelFor,
}: {
  problems: readonly FieldProblem[];
  /** What this form calls a field on screen, given the name the server used. */
  labelFor: (name: string) => string | undefined;
}) {
  if (problems.length === 0) return null;
  const named = namedProblems(problems, labelFor);

  return (
    <Notice tone="danger">
      {named.length === 1
        ? "One field needs attention:"
        : `${named.length} things need attention:`}
      <ul className="mt-1 list-disc pl-4">
        {named.map((problem, index) => (
          // Index as the key: two problems can carry the same field and the
          // same message, and nothing here reorders.
          <li key={index}>
            {problem.field && <span className="font-medium">{problem.field}</span>}
            {problem.field && " — "}
            {problem.message}
          </li>
        ))}
      </ul>
    </Notice>
  );
}
